<template>
    <div v-bind="$attrs" class="rounded-md bg-destructive/5 p-4">
        <div class="flex items-start justify-between">
            <div class="text-sm text-destructive gap-2">
                <div class="flex rounded bg-destructive/10 px-2 py-1 gap-2">
                    <div class="shrink-0">
                        <AlertTriangle class="w-5 h-5 text-destructive" aria-hidden="true" />
                    </div>
                    <p class="text-sm text-destructive truncate max-w-[25vw]" v-html="sanitizeErrorText(introductionText)"></p>
                </div>
            </div>
            <Button variant="link" class="ml-4 text-md text-destructive p-0 h-auto whitespace-nowrap" @click="onViewDetailsClick"
                >View details</Button
            >
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useErrorDialog } from '@/composables/useDialog';
import SentryService from '../services/sentry.service';
import { AlertTriangle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';

const defaultIntro = 'An error occured.';
const props = defineProps({
    error: {
        required: true
    },
    toSentry: {
        type: Boolean,
        default: false
    },
    context: {
        type: String,
        default: ''
    },
    introduction: {
        type: String,
        default: defaultIntro
    }
});

const parsedError = computed(() => {
    if (typeof props.error === 'string') return props.error;

    // Handle 422 validation errors with detail array
    const detail = props.error?.response?.data?.detail || props.error?.response?.data?.details;
    if (Array.isArray(detail)) {
        return detail.map((item, index) => `${index + 1}. ${JSON.stringify(item)}`).join('\n');
    }

    return (
        props.error?.response?.data?.error ||
        props.error?.response?.data?.detail ||
        props.error?.response?.data?.details ||
        props.error?.message ||
        String(props.error)
    );
});

const introductionText = computed(() => {
    return props.introduction === defaultIntro && parsedError.value ? parsedError.value : props.introduction;
});

function sanitizeErrorText(text) {
    if (!text) return '';
    
    // Check if text already contains HTML tags (from backend)
    const containsHtml = /<[^>]+>/g.test(text);
    
    if (containsHtml) {
        // Clean up malformed HTML and return properly formatted HTML
        return text
            // Fix malformed class attributes that got cut off mid-way
            .replace(/class="[^"]*"[^"]*"[^"]*"/g, match => {
                const classMatch = match.match(/class="([^"]+)"/);
                return classMatch ? `class="${classMatch[1]}"` : '';
            })
            // Remove orphaned quotes and text fragments after class attributes
            .replace(/"\s*'[^']*$/g, '"')
            .replace(/class="[^"]*"[^<>]*(?=\s|$)/g, match => {
                const classMatch = match.match(/class="([^"]+)"/);
                return classMatch ? `class="${classMatch[1]}"` : '';
            })
            // Clean up any remaining malformed attributes
            .replace(/\s+class="[^"]*"[^"]*'[^']*'/g, '');
    }
    
    // Return plain text (will be escaped by Vue)
    return text;
}

onMounted(() => {
    if (props.toSentry && parsedError.value) {
        SentryService.notify(new Error(parsedError.value), {
            severity: 'error',
            context: props.context
        });
    }
});

function onViewDetailsClick() {
    useErrorDialog({ errors: [parsedError.value] });
}
</script>
